import { supabase } from './supabase';
import type { Database } from '@/shared/types/database.types';

// Extended user type with is_admin field (database schema updated but types not regenerated)
type UserWithAdmin = Database['public']['Tables']['users']['Row'] & {
  is_admin?: boolean;
};

export const authService = {
  /**
   * Sign in with email and password
   */
  async signIn(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;
    return data;
  },

  /**
   * Sign out current user
   */
  async signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  /**
   * Get current session
   */
  async getSession() {
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    return data.session;
  },

  /**
   * Get current user profile with location assignments
   * Optimized: Single query with join instead of two sequential queries
   */
  async getUserProfile(userId: string) {
    const startTime = performance.now();
    const { data, error } = await supabase
      .from('users')
      .select(`
        *,
        user_locations (
          location_id,
          locations (*)
        )
      `)
      .eq('id', userId)
      .single();

    if (error || !data) {
      const duration = performance.now() - startTime;
      console.log(`⏱️ getUserProfile (optimized: 1 query): ${duration.toFixed(2)}ms`);
      throw new Error(error?.message || 'User not found');
    }

    // Note: Database types may not be regenerated yet, using data directly
    const user = data as UserWithAdmin & {
      user_locations?: Array<{
        location_id: string;
        locations: Database['public']['Tables']['locations']['Row'] | null;
      }>;
    };

    // Extract locations from the joined data
    const locations = (user.user_locations || [])
      .map((ul) => ul.locations)
      .filter((loc): loc is Database['public']['Tables']['locations']['Row'] => loc !== null);

    // Explicitly construct the return object
    const duration = performance.now() - startTime;
    console.log(`⏱️ getUserProfile (optimized: 1 query): ${duration.toFixed(2)}ms`);
    return {
      id: user.id,
      email: user.email,
      name: user.name,
      phone_number: (user as Record<string, unknown>).phone_number as string | null ?? null,
      notification_emails: ((user as Record<string, unknown>).notification_emails as string[] | null) ?? [],
      is_active: user.is_active,
      is_admin: user.is_admin || false,
      created_at: user.created_at,
      updated_at: user.updated_at,
      locations,
    };
  },

  /**
   * Reset password for email
   */
  async resetPassword(email: string) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) throw error;
  },

  /**
   * Update password
   */
  async updatePassword(newPassword: string) {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) throw error;
  },
};
